import { useMemo } from 'react';
import type { BuildState, BuildDerived } from '@/types';

/**
 * Ranging forgiveness — "how far can I mis-range and still hit vitals?"
 *
 * When you range a target at R' but it's actually at R, your pin is set for R',
 * so the arrow misses vertically by the drop the sight didn't compensate:
 *     vertical_miss(R, R') = drop(R) − (R/R')·drop(R')
 * (relative to the line of sight zeroed at R'). The forgiveness at range R for a
 * vital zone of height H is then (H/2) ÷ the inches-of-miss per yard of ranging
 * error — i.e. how many yards off you can be and still keep the arrow in the
 * kill zone. It shrinks fast with distance because the drop curve steepens, and
 * it grows with arrow speed (a flatter trajectory is more forgiving).
 *
 * Drop comes from the same validated velocity decay the kill verdict uses
 * (~89% velocity retained at 75yd). Vital-zone heights are standard bowhunting
 * shot-placement figures (heart/lung): whitetail ~9", elk ~15".
 */

const RETAIN_PER_YARD = Math.pow(0.89, 1 / 75);
const G = 32.174;
const SHOW_RANGES = [30, 40, 50, 60, 70, 80];

interface Props {
  state: BuildState;
  derived: BuildDerived;
}

/** Gravity drop (inches) at each whole yard 0..100, from the velocity model. */
function dropCurve(muzzle: number): number[] {
  const drops: number[] = [0];
  let t = 0;
  for (let yd = 1; yd <= 100; yd++) {
    const vMid = muzzle * Math.pow(RETAIN_PER_YARD, yd - 0.5);
    t += 3 / vMid; // 3 ft per yard ÷ fps = seconds
    drops[yd] = 0.5 * G * t * t * 12;
  }
  return drops;
}

export function RangingForgiveness({ state, derived }: Props) {
  void derived; // (kept for future per-build vital tuning)
  const { rows, example } = useMemo(() => {
    const drop = dropCurve(state.fps);
    // Inches of vertical miss per 1 yard of ranging error at range R.
    const missPerYard = (R: number) => {
      if (R < 2) return 0;
      const lo = R - 1;
      return Math.abs(drop[R] - (R / lo) * drop[lo]);
    };
    const forgive = (R: number, vitalIn: number) => {
      const m = missPerYard(R);
      return m > 0 ? (vitalIn / 2) / m : 99;
    };
    const rows = SHOW_RANGES.map((R) => ({
      R,
      deer: forgive(R, 9),
      elk: forgive(R, 15),
    }));
    // Worked example: ranged 76, actually 80.
    const exMiss = Math.abs(drop[80] - (80 / 76) * drop[76]);
    return { rows, example: exMiss };
  }, [state.fps]);

  const fmt = (y: number) => (y >= 20 ? '±20+' : `±${y.toFixed(1).replace(/\.0$/, '')}`);

  return (
    <div
      style={{
        border: '1px solid #3f3f46',
        borderRadius: 10,
        overflow: 'hidden',
        background: 'linear-gradient(180deg,#141416,#0a0a0b)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: '1px solid #27272a',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, letterSpacing: '0.04em', color: '#fafafa' }}>
          RANGING FORGIVENESS
        </h2>
        <span style={{ fontSize: 11, fontFamily: 'ui-monospace, monospace', color: '#71717a' }}>
          yards you can mis-range &amp; still hit vitals
        </span>
      </div>

      <div style={{ padding: '4px 8px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ color: '#71717a', fontSize: 10, fontFamily: 'ui-monospace, monospace', letterSpacing: '0.06em' }}>
              <th style={{ textAlign: 'left', padding: '6px 12px' }}>SHOT</th>
              <th style={{ textAlign: 'right', padding: '6px 12px' }}>DEER · 9″ VITALS</th>
              <th style={{ textAlign: 'right', padding: '6px 12px' }}>ELK · 15″ VITALS</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              return (
                <tr key={r.R} style={{ borderTop: '1px solid #1f1f23' }}>
                  <td style={{ padding: '8px 12px', color: '#e4e4e7', fontWeight: 600 }}>{r.R} yd</td>
                  <td
                    style={{
                      padding: '8px 12px',
                      textAlign: 'right',
                      fontWeight: 700,
                      color: r.deer < 3 ? '#ef4444' : r.deer < 5 ? '#f59e0b' : '#22c55e',
                    }}
                  >
                    {fmt(r.deer)} yd
                  </td>
                  <td
                    style={{
                      padding: '8px 12px',
                      textAlign: 'right',
                      fontWeight: 700,
                      color: r.elk < 3 ? '#ef4444' : r.elk < 5 ? '#f59e0b' : '#22c55e',
                    }}
                  >
                    {fmt(r.elk)} yd
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ padding: '8px 16px 12px', fontSize: 11, color: '#a1a1aa', lineHeight: 1.5 }}>
        <span style={{ color: '#f59e0b', fontWeight: 600 }}>Reality check:</span> at 80 yd, ranging it as 76
        puts your arrow <strong style={{ color: '#fafafa' }}>~{example.toFixed(0)}″ low</strong> — that's why long
        shots punish a few yards of error. Flatter (faster) arrows widen every number above.
      </div>
    </div>
  );
}
